import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, HStack } from '@chakra-ui/react';
import { DeleteIcon, DragHandleIcon } from '@chakra-ui/icons';

const BoundingBoxDrawer = ({
  onBoxDrawn,
  onBoxUpdated,
  imageDisplaySize,
  initialBoxes = []
}) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentBox, setCurrentBox] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [selectedBox, setSelectedBox] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !imageDisplaySize.width || !imageDisplaySize.height) return;

    // Debug information
    console.log('BoundingBoxDrawer initializing with:', {
      imageDisplaySize,
      initialBoxes
    });

    // Convert initial normalized boxes to screen coordinates
    const formattedBoxes = initialBoxes.map((box, index) => {
      // Ensure coordinates are valid numbers
      const x = typeof box.x === 'number' ? box.x : 0;
      const y = typeof box.y === 'number' ? box.y : 0;
      const w = typeof box.width === 'number' ? box.width : 0;
      const h = typeof box.height === 'number' ? box.height : 0;

      // Calculate dimensions in display coordinates
      const width = Math.round(w * imageDisplaySize.width);
      const height = Math.round(h * imageDisplaySize.height);
      
      // Calculate center position
      const centerX = Math.round(x * imageDisplaySize.width);
      const centerY = Math.round(y * imageDisplaySize.height);

      // Calculate top-left position
      const formattedBox = {
        left: centerX - (width / 2),
        top: centerY - (height / 2),
        width,
        height
      };

      // Debug individual box
      console.log(`Initial Box #${index}:`, {
        original: { x, y, w, h },
        calculated: { centerX, centerY, ...formattedBox }
      });

      return formattedBox;
    });
    
    setBoxes(formattedBoxes);
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, [imageDisplaySize.width, imageDisplaySize.height, initialBoxes]);

  const normalizeCoordinates = (screenX, screenY) => {
    if (!containerRef.current) return { x: 0, y: 0 };

    const rect = containerRef.current.getBoundingClientRect();
    let x = (screenX - rect.left) / rect.width * imageDisplaySize.width;
    let y = (screenY - rect.top) / rect.height * imageDisplaySize.height;
    
    return {
      x: Math.max(0, Math.min(x, imageDisplaySize.width)),
      y: Math.max(0, Math.min(y, imageDisplaySize.height))
    };
  };

  const normalizeBox = (box) => {
    const normalized = {
      x: (box.left + box.width / 2) / imageDisplaySize.width,
      y: (box.top + box.height / 2) / imageDisplaySize.height,
      width: box.width / imageDisplaySize.width,
      height: box.height / imageDisplaySize.height
    };
    
    // Debug normalization
    console.log('Normalizing box:', {
      from: box,
      to: normalized,
      imageDisplaySize
    });
    
    return normalized;
  };

  const handlePointerDown = (e) => {
    if (!isReady) return;
    e.preventDefault();
    e.stopPropagation();

    const pos = normalizeCoordinates(e.clientX, e.clientY);
    
    const clickedBoxIndex = boxes.findIndex(box => (
      pos.x >= box.left && 
      pos.x <= box.left + box.width &&
      pos.y >= box.top && 
      pos.y <= box.top + box.height
    ));

    if (clickedBoxIndex !== -1) {
      setSelectedBox(boxes[clickedBoxIndex]);
      setIsDragging(true);
      setDragStart(pos);
    } else {
      setIsDrawing(true);
      setStartPos(pos);
      setSelectedBox(null);
    }

    containerRef.current?.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isReady || (!isDrawing && !isDragging)) return;
    e.preventDefault();
    e.stopPropagation();

    const pos = normalizeCoordinates(e.clientX, e.clientY);

    if (isDragging && selectedBox) {
      const dx = pos.x - dragStart.x;
      const dy = pos.y - dragStart.y;
      
      setBoxes(prevBoxes => 
        prevBoxes.map(box => {
          if (box === selectedBox) {
            const newLeft = Math.max(0, Math.min(box.left + dx, imageDisplaySize.width - box.width));
            const newTop = Math.max(0, Math.min(box.top + dy, imageDisplaySize.height - box.height));
            const updatedBox = { ...box, left: newLeft, top: newTop };
            
            if (onBoxUpdated) {
              const boxIndex = prevBoxes.indexOf(box);
              onBoxUpdated(normalizeBox(updatedBox), boxIndex);
            }
            return updatedBox;
          }
          return box;
        })
      );
      setDragStart(pos);
    } else if (isDrawing) {
      const left = Math.min(pos.x, startPos.x);
      const top = Math.min(pos.y, startPos.y);
      const width = Math.abs(pos.x - startPos.x);
      const height = Math.abs(pos.y - startPos.y);
      
      setCurrentBox({
        left,
        top,
        width: Math.min(width, imageDisplaySize.width - left),
        height: Math.min(height, imageDisplaySize.height - top)
      });
    }
  };

  const handlePointerUp = (e) => {
    if (!isReady) return;
    e.preventDefault();
    e.stopPropagation();

    if (isDragging) {
      setIsDragging(false);
      setDragStart(null);
    } else if (isDrawing && currentBox) {
      if (currentBox.width > 5 && currentBox.height > 5) {
        setBoxes(prev => [...prev, currentBox]);
        if (onBoxDrawn) {
          onBoxDrawn(normalizeBox(currentBox));
        }
      }
    }
    
    setIsDrawing(false);
    setCurrentBox(null);
    containerRef.current?.releasePointerCapture(e.pointerId);
  };

  const handlePointerLeave = (e) => {
    if (!isReady) return;
    e.preventDefault();
    e.stopPropagation();
    if (isDrawing || isDragging) {
      handlePointerUp(e);
    }
  };

  const handleDelete = (boxToDelete) => {
    const boxIndex = boxes.indexOf(boxToDelete);
    setBoxes(prev => prev.filter(b => b !== boxToDelete));
    setSelectedBox(null);
    
    if (onBoxUpdated && boxIndex !== -1) {
      onBoxUpdated(null, boxIndex);
    }
  };

  const renderBox = (box, i, isPreview = false) => (
    <Box
      key={isPreview ? 'preview' : i}
      position="absolute"
      style={{
        left: `${box.left}px`,
        top: `${box.top}px`,
        width: `${box.width}px`,
        height: `${box.height}px`
      }}
      borderWidth="2px" // Use theme token implicitly
      borderStyle="solid"
      borderColor={box === selectedBox ? "warning" : "brand.primary"} // Use theme tokens
      backgroundColor={isPreview ? "brand.primary" : "transparent"} // Use theme token
      opacity={isPreview ? 0.3 : 1}
      cursor={isDragging && box === selectedBox ? "move" : "pointer"}
      _hover={{ borderColor: "warning" }} // Use theme token
      onClick={(e) => e.stopPropagation()}
    >
      {box === selectedBox && (
        <HStack
          position="absolute"
          top="-30px"
          left="0"
          spacing={1}
          zIndex={1}
        >
          <IconButton
            icon={<DragHandleIcon />}
            size="xs"
            colorScheme="brand.primary" // Use theme scheme
            cursor="move"
            aria-label="Drag box"
          />
          <IconButton
            icon={<DeleteIcon />}
            size="xs"
            colorScheme="error" // Use theme scheme
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(box);
            }}
            aria-label="Delete box"
          />
        </HStack>
      )}
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      position="absolute"
      top="0"
      left="0"
      width="100%"
      height="100%"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      cursor={isReady ? "crosshair" : "default"}
      touchAction="none"
      userSelect="none"
      onClick={(e) => e.stopPropagation()}
      pointerEvents={isReady ? "auto" : "none"}
    >
      {boxes.map((box, i) => renderBox(box, i))}
      {currentBox && renderBox(currentBox, -1, true)}
    </Box>
  );
};

export default BoundingBoxDrawer;
